// src/pages/admin/users/UsersAdmin.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Typography,
  Input,
  Button,
  Alert,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Select,
  Option,
} from "@material-tailwind/react";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../../../firebaseConfig";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  deleteDoc,
  updateDoc,
} from "firebase/firestore";
import {
  ArrowPathIcon,
  ArrowRightIcon,
  MagnifyingGlassIcon,
  DocumentTextIcon,
  BanknotesIcon,
  TrashIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";

/* ✅ ألوان الصورة فقط (Grayscale) */
const BRAND = {
  ink: "#2F2F2F",
  graphite: "#878787",
  silver: "#B8B8B8",
  mist: "#D0D0D0",
  paper: "#EFEFEF",
  white: "#FCFCFC",
};

const ui = {
  pageBg: `radial-gradient(1200px 520px at 90% -30%, ${BRAND.paper}, ${BRAND.white} 55%)`,
  headerBg: `linear-gradient(135deg, ${BRAND.ink}, ${BRAND.graphite})`,
  cardBorder: `1px solid ${BRAND.paper}`,
  cardShadow: "0 18px 50px rgba(0,0,0,0.08), 0 6px 18px rgba(0,0,0,0.05)",
  softShadow: "0 10px 26px rgba(0,0,0,0.06)",
};

function RoleBadge({ isAdmin }) {
  return (
    <span
      className="inline-flex items-center rounded-full px-3 py-1 text-xs font-extrabold"
      style={{
        background: isAdmin ? BRAND.paper : BRAND.white,
        border: `1px solid ${BRAND.mist}`,
        color: BRAND.ink,
      }}
    >
      {isAdmin ? "أدمن" : "موظّف"}
    </span>
  );
}

export default function UsersAdmin() {
  const [me, setMe] = useState(null);
  const [role, setRole] = useState(Number(localStorage.getItem("role") || "2"));
  const isAdmin = role === 1;

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [q, setQ] = useState("");

  // ✅ حذف + مودال تأكيد
  const [delOpen, setDelOpen] = useState(false);
  const [delUser, setDelUser] = useState(null);
  const [delBusy, setDelBusy] = useState(false);
  const [delErr, setDelErr] = useState("");

  // ✅ تعديل + مودال
  const [editOpen, setEditOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editBusy, setEditBusy] = useState(false);
  const [editErr, setEditErr] = useState("");
  const [editOk, setEditOk] = useState("");

  const [editForm, setEditForm] = useState({
    storeName: "",
    name: "",
    phone: "",
    role: "2",
  });

  const nav = useNavigate();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setMe(u || null);
      if (u?.uid) {
        try {
          const snap = await getDoc(doc(db, "users", u.uid));
          const r =
            snap.exists() && snap.data().role !== undefined
              ? Number(snap.data().role)
              : Number(localStorage.getItem("role") || "2");
          setRole(r);
          localStorage.setItem("role", String(r));
        } catch {}
      }
    });
    return () => unsub();
  }, []);

  async function loadUsers() {
    if (!isAdmin) return;
    setErr("");
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, "users"));
      const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
      list.sort((a, b) =>
        String(a.storeName || a.displayName || a.name || a.email || "").localeCompare(
          String(b.storeName || b.displayName || b.name || b.email || ""),
          "ar"
        )
      );
      setUsers(list);
    } catch (e) {
      console.error(e);
      setErr("تعذّر جلب المستخدمين.");
    } finally {
      setLoading(false);
    }
  }

  async function loadStatsForAll() {
    if (!isAdmin) return;
    setLoading(true);
    try {
      for (const u of users) {
        // إحصائيات الفواتير
        let invoicesCount = 0;
        let invoicesTotal = 0;

        try {
          const invSnap = await getDocs(collection(db, "users", u.id, "invoices"));
          invoicesCount = invSnap.size;
          invSnap.forEach((d) => {
            const v = d.data() || {};
            const items = Array.isArray(v.items) ? v.items : [];
            const subtotal = items.reduce(
              (s, it) => s + Number(it.price || 0) * Number(it.qty || 0),
              0
            );
            const discount = Number(v.discount || 0);
            const total = Number(
              v.total || Math.max(0, subtotal - (subtotal * discount) / 100)
            );
            invoicesTotal += total;
          });
        } catch {}

        // رصيد الصندوق
        let cashboxBalance = 0;
        try {
          const cbSnap = await getDocs(collection(db, "users", u.id, "cashbox"));
          cbSnap.forEach((d) => {
            const v = d.data() || {};
            const amt = Number(v.amount || 0);
            cashboxBalance += v.kind === "payment" ? -amt : amt;
          });
        } catch {}

        setStats((prev) => ({
          ...prev,
          [u.id]: { invoicesCount, invoicesTotal, cashboxBalance },
        }));
      }
    } catch (e) {
      console.error(e);
      setErr("تعذّر حساب الإحصائيات.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (isAdmin) loadUsers();
    // eslint-disable-next-line
  }, [isAdmin]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return users;
    return users.filter((u) => {
      const name = String(u.storeName || u.displayName || u.name || "").toLowerCase();
      const email = String(u.email || "").toLowerCase();
      const phone = String(u.phone || u.customerPhone || "").toLowerCase();
      const id = String(u.id || "").toLowerCase();
      return name.includes(s) || email.includes(s) || phone.includes(s) || id.includes(s);
    });
  }, [users, q]);

  function goBack() {
    if (window.history.length > 1) nav(-1);
    else nav("/");
  }

  // ----------------- حذف -----------------
  function openDeleteModal(u) {
    setDelErr("");
    setDelUser(u);
    setDelOpen(true);
  }

  function closeDeleteModal() {
    if (delBusy) return;
    setDelOpen(false);
    setDelUser(null);
    setDelErr("");
  }

  async function confirmDelete() {
    if (!delUser?.id) return;
    if (delUser.id === me?.uid) {
      setDelErr("لا يمكنك حذف حسابك الحالي.");
      return;
    }

    setDelBusy(true);
    setDelErr("");
    try {
      await deleteDoc(doc(db, "users", delUser.id));

      setUsers((prev) => prev.filter((x) => x.id !== delUser.id));
      setStats((prev) => {
        const next = { ...prev };
        delete next[delUser.id];
        return next;
      });

      setDelOpen(false);
      setDelUser(null);
    } catch (e) {
      console.error(e);
      setDelErr("تعذّر حذف المستخدم.");
    } finally {
      setDelBusy(false);
    }
  }

  // ----------------- تعديل -----------------
  function openEditModal(u) {
    setEditErr("");
    setEditOk("");
    setEditUser(u);
    setEditForm({
      storeName: String(u.storeName || ""),
      name: String(u.displayName || u.name || ""),
      phone: String(u.phone || ""),
      role: String(u.role !== undefined ? u.role : 2),
    });
    setEditOpen(true);
  }

  function closeEditModal() {
    if (editBusy) return;
    setEditOpen(false);
    setEditUser(null);
    setEditErr("");
    setEditOk("");
  }

  async function confirmEdit() {
    if (!editUser?.id) return;

    setEditBusy(true);
    setEditErr("");
    setEditOk("");
    try {
      const payload = {
        storeName: editForm.storeName.trim(),
        displayName: editForm.name.trim(),
        name: editForm.name.trim(), // ✅ إذا تعتمد name بمكان آخر
        phone: editForm.phone.trim(),
        role: Number(editForm.role || "2"),
      };

      await updateDoc(doc(db, "users", editUser.id), payload);

      // ✅ تحديث القائمة محلياً
      setUsers((prev) =>
        prev.map((x) => (x.id === editUser.id ? { ...x, ...payload } : x))
      );

      setEditOk("تم تحديث الحساب بنجاح.");
      // ممكن تغلقه مباشرة أو تخليه مفتوح حتى يشوف الرسالة
      // closeEditModal();
    } catch (e) {
      console.error(e);
      setEditErr("تعذّر تحديث الحساب.");
    } finally {
      setEditBusy(false);
    }
  }

  if (!isAdmin) {
    return (
      <div dir="rtl" className="p-6">
        <Alert color="red" className="font-tajawal">
          هذه الصفحة للمشرف فقط.
        </Alert>
      </div>
    );
  }

  return (
    <div dir="rtl" className="p-4 min-h-screen font-tajawal" style={{ background: ui.pageBg }}>
      <Card
        className="mx-auto max-w-7xl bg-white rounded-3xl overflow-hidden"
        style={{ border: ui.cardBorder, boxShadow: ui.cardShadow }}
      >
        <CardHeader floated={false} shadow={false} className="p-0">
          <div className="flex items-center justify-between px-6 py-6" style={{ background: ui.headerBg }}>
            <button
              onClick={goBack}
              className="group inline-flex items-center gap-2 rounded-2xl px-3 py-2 transition"
              style={{
                background: "rgba(255,255,255,0.10)",
                border: "1px solid rgba(255,255,255,0.18)",
                color: BRAND.white,
              }}
              title="رجوع"
            >
              <ArrowRightIcon className="h-5 w-5" />
              <span className="text-sm font-extrabold">رجوع</span>
            </button>

            <div className="text-center flex-1">
              <Typography variant="h4" className="text-white font-extrabold">
                المستخدمون
              </Typography>
              <div
                className="mt-2 inline-flex items-center gap-2 rounded-full px-4 py-1 text-xs"
                style={{
                  background: "rgba(255,255,255,0.10)",
                  border: "1px solid rgba(255,255,255,0.18)",
                  color: "rgba(255,255,255,0.85)",
                  fontWeight: 800,
                }}
              >
                <span>العدد:</span>
                <span>{filtered.length}</span>
              </div>
            </div>

            <span className="w-[92px]" />
          </div>
        </CardHeader>

        <CardBody className="space-y-4 p-6">
          {err && (
            <Alert color="red" className="font-tajawal" style={{ borderRadius: 16 }}>
              {err}
            </Alert>
          )}

          <div
            className="flex flex-wrap items-end gap-3 rounded-2xl p-4"
            style={{
              background: BRAND.white,
              border: `1px solid ${BRAND.paper}`,
              boxShadow: ui.softShadow,
            }}
          >
            <div className="flex-1 min-w-[240px]">
              <Input
                label="بحث بالاسم/البريد/الهاتف/المعرف"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                icon={<MagnifyingGlassIcon className="h-5 w-5" />}
                crossOrigin=""
                className="font-tajawal"
                labelProps={{ className: "font-tajawal" }}
              />
            </div>

            <Button
              variant="outlined"
              onClick={loadUsers}
              disabled={loading}
              className="flex items-center gap-2 font-tajawal rounded-2xl"
              style={{ borderColor: BRAND.mist, color: BRAND.ink }}
            >
              <ArrowPathIcon className="h-5 w-5" />
              إعادة تحميل
            </Button>

            <Button
              onClick={loadStatsForAll}
              disabled={loading || !users.length}
              className="flex items-center gap-2 font-tajawal rounded-2xl"
              style={{
                background: `linear-gradient(135deg, ${BRAND.ink}, ${BRAND.graphite})`,
                color: "white",
              }}
            >
              <ArrowPathIcon className="h-5 w-5" />
              تحديث الإحصائيات
            </Button>
          </div>

          <div
            className="overflow-auto rounded-2xl"
            style={{ border: `1px solid ${BRAND.paper}`, boxShadow: ui.softShadow }}
          >
            <table className="w-full text-right">
              <thead>
                <tr style={{ backgroundColor: BRAND.paper }}>
                  {[
                    "المستخدم",
                    "البريد/الهاتف",
                    "الدور",
                    "عدد الفواتير",
                    "إجمالي المبيعات",
                    "رصيد الصندوق",
                    "إجراءات",
                  ].map((h) => (
                    <th
                      key={h}
                      className="p-3 text-sm font-extrabold"
                      style={{
                        color: BRAND.ink,
                        borderBottom: `1px solid ${BRAND.mist}`,
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {filtered.length ? (
                  filtered.map((u, idx) => {
                    const s = stats[u.id] || {
                      invoicesCount: 0,
                      invoicesTotal: 0,
                      cashboxBalance: 0,
                    };
                    const name = u.storeName || u.displayName || u.name || u.email || u.id;

                    return (
                      <tr
                        key={u.id}
                        className="border-t transition"
                        style={{
                          borderColor: BRAND.paper,
                          background: idx % 2 === 0 ? BRAND.white : "#FFFFFF",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(239,239,239,0.65)")}
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background = idx % 2 === 0 ? BRAND.white : "#FFFFFF")
                        }
                      >
                        <td className="p-3">
                          <div className="font-extrabold" style={{ color: BRAND.ink }}>
                            {name}
                          </div>
                          <div className="text-xs" style={{ color: BRAND.graphite }}>
                            UID: {u.id}
                          </div>
                        </td>

                        <td className="p-3">
                          <div style={{ color: BRAND.ink }}>{u.email || "-"}</div>
                          <div className="text-xs" style={{ color: BRAND.graphite }}>
                            {u.phone || "-"}
                          </div>
                        </td>

                        <td className="p-3">
                          <RoleBadge isAdmin={Number(u.role) === 1} />
                        </td>

                        <td className="p-3" style={{ color: BRAND.ink }}>
                          {s.invoicesCount}
                        </td>

                        <td className="p-3" style={{ color: BRAND.ink, fontWeight: 900 }}>
                          {Number(s.invoicesTotal).toLocaleString("en-EG")} د.ع
                        </td>

                        <td
                          className="p-3"
                          style={{
                            color: s.cashboxBalance < 0 ? "#7f1d1d" : BRAND.ink,
                            fontWeight: 900,
                          }}
                        >
                          {Number(s.cashboxBalance).toLocaleString("en-EG")} د.ع
                        </td>

                        <td className="p-3">
                          <div className="flex flex-wrap gap-2">
                            <Button
                              size="sm"
                              variant="outlined"
                              onClick={() => nav(`/invoice?uid=${u.id}`)}
                              className="flex items-center gap-2 font-tajawal rounded-2xl"
                              style={{ borderColor: BRAND.mist, color: BRAND.ink }}
                            >
                              <DocumentTextIcon className="h-5 w-5" />
                              فواتيره
                            </Button>

                            <Button
                              size="sm"
                              variant="outlined"
                              onClick={() => nav(`/cashbox?uid=${u.id}`)}
                              className="flex items-center gap-2 font-tajawal rounded-2xl"
                              style={{ borderColor: BRAND.mist, color: BRAND.ink }}
                            >
                              <BanknotesIcon className="h-5 w-5" />
                              صندوقه
                            </Button>

                            {/* ✅ تعديل الحساب */}
                            <Button
                              size="sm"
                              variant="outlined"
                              onClick={() => openEditModal(u)}
                              disabled={loading || editBusy}
                              className="flex items-center gap-2 font-tajawal rounded-2xl"
                              style={{ borderColor: BRAND.mist, color: BRAND.ink }}
                              title="تعديل الحساب"
                            >
                              <PencilSquareIcon className="h-5 w-5" />
                              تعديل
                            </Button>

                            {/* ✅ حذف */}
                            <Button
                              size="sm"
                              variant="outlined"
                              onClick={() => openDeleteModal(u)}
                              disabled={loading || delBusy || u.id === me?.uid}
                              className="flex items-center gap-2 font-tajawal rounded-2xl"
                              style={{
                                borderColor: BRAND.mist,
                                color: "#7f1d1d",
                              }}
                              title={u.id === me?.uid ? "لا يمكن حذف حسابك" : "حذف المستخدم"}
                            >
                              <TrashIcon className="h-5 w-5" />
                              حذف
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={7} className="p-6 text-center" style={{ color: BRAND.graphite }}>
                      لا يوجد نتائج.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>

      {/* ✅ مودال تعديل الحساب */}
      <Dialog open={editOpen} handler={closeEditModal} size="sm">
        <DialogHeader className="font-tajawal" style={{ color: BRAND.ink }}>
          تعديل الحساب
        </DialogHeader>

        <DialogBody className="font-tajawal">
          {editErr && (
            <Alert color="red" className="mb-4 font-tajawal" style={{ borderRadius: 16 }}>
              {editErr}
            </Alert>
          )}
          {editOk && (
            <Alert color="green" className="mb-4 font-tajawal" style={{ borderRadius: 16 }}>
              {editOk}
            </Alert>
          )}

          <div className="space-y-4">
            <div
              className="rounded-2xl p-4"
              style={{ border: `1px solid ${BRAND.paper}`, background: BRAND.white }}
            >
              <div className="text-xs" style={{ color: BRAND.graphite }}>
                UID: {editUser?.id || "-"}
              </div>
              <div className="text-xs mt-1" style={{ color: BRAND.graphite }}>
                البريد (قراءة فقط): {editUser?.email || "-"}
              </div>
              <div className="text-xs mt-1" style={{ color: BRAND.graphite }}>
                * تعديل البريد الحقيقي في Authentication يحتاج Cloud Function
              </div>
            </div>

            <Input
              label="اسم المتجر"
              value={editForm.storeName}
              onChange={(e) => setEditForm((p) => ({ ...p, storeName: e.target.value }))}
              crossOrigin=""
              className="font-tajawal"
              labelProps={{ className: "font-tajawal" }}
            />

            <Input
              label="اسم المستخدم"
              value={editForm.name}
              onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
              crossOrigin=""
              className="font-tajawal"
              labelProps={{ className: "font-tajawal" }}
            />

            <Input
              label="الهاتف"
              value={editForm.phone}
              onChange={(e) => setEditForm((p) => ({ ...p, phone: e.target.value }))}
              crossOrigin=""
              className="font-tajawal"
              labelProps={{ className: "font-tajawal" }}
            />

            <div>
              <Typography className="mb-2 text-sm font-extrabold" style={{ color: BRAND.ink }}>
                الدور
              </Typography>
              <Select
                value={String(editForm.role)}
                onChange={(v) => setEditForm((p) => ({ ...p, role: String(v || "2") }))}
                className="font-tajawal"
                labelProps={{ className: "font-tajawal" }}
              >
                <Option value="1">أدمن</Option>
                <Option value="2">موظّف</Option>
              </Select>
            </div>
          </div>
        </DialogBody>

        <DialogFooter className="gap-2">
          <Button
            variant="outlined"
            onClick={closeEditModal}
            disabled={editBusy}
            className="rounded-2xl font-tajawal"
            style={{ borderColor: BRAND.mist, color: BRAND.ink }}
          >
            إلغاء
          </Button>

          <Button
            onClick={confirmEdit}
            disabled={editBusy || !editUser?.id}
            className="rounded-2xl font-tajawal flex items-center gap-2"
            style={{
              background: `linear-gradient(135deg, ${BRAND.ink}, ${BRAND.graphite})`,
              color: "white",
            }}
          >
            <PencilSquareIcon className="h-5 w-5" />
            {editBusy ? "جارٍ الحفظ..." : "حفظ التعديل"}
          </Button>
        </DialogFooter>
      </Dialog>

      {/* ✅ مودال تأكيد الحذف */}
      <Dialog open={delOpen} handler={closeDeleteModal} size="sm">
        <DialogHeader className="font-tajawal" style={{ color: BRAND.ink }}>
          تأكيد الحذف
        </DialogHeader>

        <DialogBody className="font-tajawal">
          {delErr && (
            <Alert color="red" className="mb-4 font-tajawal" style={{ borderRadius: 16 }}>
              {delErr}
            </Alert>
          )}

          <div className="space-y-2">
            <Typography className="font-tajawal font-bold" style={{ color: BRAND.ink }}>
              هل أنت متأكد من حذف هذا المستخدم؟
            </Typography>

            <div
              className="rounded-2xl p-4"
              style={{ border: `1px solid ${BRAND.paper}`, background: BRAND.white }}
            >
              <div className="font-extrabold" style={{ color: BRAND.ink }}>
                {delUser?.storeName ||
                  delUser?.displayName ||
                  delUser?.name ||
                  delUser?.email ||
                  delUser?.id ||
                  "-"}
              </div>
              <div className="text-xs mt-1" style={{ color: BRAND.graphite }}>
                UID: {delUser?.id || "-"}
              </div>
              <div className="text-xs mt-1" style={{ color: BRAND.graphite }}>
                البريد: {delUser?.email || "-"} — الهاتف: {delUser?.phone || "-"}
              </div>
            </div>

            <Typography className="text-sm font-tajawal" style={{ color: BRAND.graphite }}>
              سيتم حذف مستند المستخدم من قاعدة البيانات. (حذف حساب تسجيل الدخول أو بياناته الفرعية يحتاج Admin SDK)
            </Typography>
          </div>
        </DialogBody>

        <DialogFooter className="gap-2">
          <Button
            variant="outlined"
            onClick={closeDeleteModal}
            disabled={delBusy}
            className="rounded-2xl font-tajawal"
            style={{ borderColor: BRAND.mist, color: BRAND.ink }}
          >
            إلغاء
          </Button>

          <Button
            onClick={confirmDelete}
            disabled={delBusy || !delUser?.id}
            className="rounded-2xl font-tajawal flex items-center gap-2"
            style={{
              background: "linear-gradient(135deg, #7f1d1d, #991b1b)",
              color: "white",
            }}
          >
            <TrashIcon className="h-5 w-5" />
            {delBusy ? "جارٍ الحذف..." : "تأكيد الحذف"}
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
}
